import React, { useEffect, useMemo, useRef, useState } from 'react'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import MortalityMatrix, {
  getMortalityMatrixStatusPresentation,
  type MortalityMatrixStatusPresentation
} from '../components/MortalityMatrix'
import { MatrixData } from '../components/MatrixTable'
import GradientButton from '../components/GradientButton'
import { type ProductionSpeciesKey, loadStoredSpecies, persistSpeciesSelection } from '../data/productionSpeciesPresets'
import { useLots } from '../hooks/useLots'
import { useTwoStepConfirm } from '../hooks/useTwoStepConfirm'
import {
  cacheMatrix,
  cloneMatrix as cloneMortalityMatrix,
  getDefaultMatrixForSpecies,
  interpolateMortalityRate,
  markLocalMortalityMatrixEdit
} from '../utils/mortalityMatrix'
import { resolveSfrMatrixPresetKey } from '../utils/sfrMatrix'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import { findFarmByIdOrName, findProductionUnitByIdOrName } from '../utils/contextSelection'
import { findLotByDashboardSelection, normalizeDashboardLotSelection } from '../utils/dashboardLotSelection'
import { saveEditedFarm } from '../utils/editFarmApi'
import { resolveFarmProductionSpeciesKey } from '../utils/farmProductionSpecies'
import { ensureFarmList, readCurrentFarm, type WizardFarm } from '../utils/farmWizard'
import { createId } from '../utils/id'
import { lotApi } from '../utils/lotApi'
import { isLotActiveForInteraction } from '../utils/lotInteraction'
import {
  findFarmMortalityProfile,
  listFarmMortalityProfiles,
  readAssignedMortalityProfileId,
  readFarmDefaultMortalityProfileId,
  resolveMortalityProfileSelection,
  setFarmDefaultMortalityProfileId,
  setLotMortalityProfileAssignment,
  setProductionUnitMortalityProfileAssignment,
  upsertFarmMortalityProfile
} from '../utils/mortalityProfiles'
import { useT, type TranslationKey } from '../i18n'

const SPECIES_KEYS: ProductionSpeciesKey[] = ['trout', 'salmon', 'tilapia', 'shrimp']

const SPECIES_I18N: Record<(typeof SPECIES_KEYS)[number], { labelKey: TranslationKey; descriptionKey: TranslationKey }> = {
  trout: {
    labelKey: 'sfrManagement.species.trout.label',
    descriptionKey: 'mortalityManagement.species.trout.description'
  },
  salmon: {
    labelKey: 'sfrManagement.species.salmon.label',
    descriptionKey: 'mortalityManagement.species.salmon.description'
  },
  tilapia: {
    labelKey: 'sfrManagement.species.tilapia.label',
    descriptionKey: 'mortalityManagement.species.tilapia.description'
  },
  shrimp: {
    labelKey: 'sfrManagement.species.shrimp.label',
    descriptionKey: 'mortalityManagement.species.shrimp.description'
  }
}

const PROFILE_EDITOR_SPECIES_DEFAULT = '__species_default__'
const PROFILE_SAVE_DEBOUNCE_MS = 600
// resetToDefaults has two branches (with/without a selectedProfile) that
// share the same physical "Reset" button — a single sentinel id arms both,
// mirroring SFRManagement.tsx's identical pattern.
const RESET_CONFIRM_ID = 'mortality-reset-to-defaults'

const matricesEqual = (left: MatrixData, right: MatrixData): boolean => JSON.stringify(left) === JSON.stringify(right)

const statusStyle = (tone: MortalityMatrixStatusPresentation['tone']): React.CSSProperties => {
  const borderColor = tone === 'success'
    ? '#86efac'
    : tone === 'error'
      ? '#fecaca'
      : '#bae6fd'
  const backgroundColor = tone === 'success'
    ? '#f0fdf4'
    : tone === 'error'
      ? '#fef2f2'
      : '#f0f9ff'
  const textColor = tone === 'success'
    ? '#166534'
    : tone === 'error'
      ? '#b91c1c'
      : '#075985'

  return {
    marginBottom: '20px',
    padding: '12px 16px',
    borderRadius: '10px',
    border: `1px solid ${borderColor}`,
    background: backgroundColor,
    color: textColor,
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '1.5'
  }
}

export const resolveMortalityManagementSpecies = (
  farm: WizardFarm | undefined,
  fallbackSpecies: ProductionSpeciesKey
): ProductionSpeciesKey => {
  const farmSpeciesKey = resolveFarmProductionSpeciesKey(farm)
  return farmSpeciesKey ?? fallbackSpecies
}

// Mirrors SFRManagement.tsx's `resolveSfrResetToDefaultsAction` — extracted
// as a pure function so both branches (with/without a `selectedProfile`) are
// unit-testable without mounting the full screen's data-bootstrap surface
// (farms/lots/profiles), same rationale as the SFR sibling.
export type MortalityResetToDefaultsAction = 'arm' | 'applyProfileDefaults' | 'applySpeciesDefaults'

export const resolveMortalityResetToDefaultsAction = (
  isConfirmArmed: boolean,
  hasSelectedProfile: boolean
): MortalityResetToDefaultsAction => {
  if (!isConfirmArmed) return 'arm'
  return hasSelectedProfile ? 'applyProfileDefaults' : 'applySpeciesDefaults'
}

export default function MortalityManagement() {
  const t = useT()
  const [testWeight, setTestWeight] = useState<string>('100')
  const [testTemperature, setTestTemperature] = useState<string>('15')
  const [calculatedMortality, setCalculatedMortality] = useState<number | null>(null)
  const [currentMatrixData, setCurrentMatrixData] = useState<MatrixData | null>(null)
  const [selectedSpecies, setSelectedSpecies] = useState<ProductionSpeciesKey>(() => loadStoredSpecies())
  const [selectedProfileId, setSelectedProfileId] = useState<string>(PROFILE_EDITOR_SPECIES_DEFAULT)
  const [availableFarms, setAvailableFarms] = useState<WizardFarm[]>([])
  const [farmId, setFarmId] = useState<string>('')
  const [productionUnitId, setProductionUnitId] = useState<string>('')
  const [lotId, setLotId] = useState<string>('')
  const [refreshKey, setRefreshKey] = useState<number>(0)
  const [matrixVersion, setMatrixVersion] = useState<number>(0)
  const [newWeight, setNewWeight] = useState<string>('')
  const [newTemperature, setNewTemperature] = useState<string>('')
  const [matrixStatus, setMatrixStatus] = useState<MortalityMatrixStatusPresentation | null>(null)
  const profileSaveTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const skipNextProfileSaveRef = useRef(true)

  const selectedFarm = useMemo(() => findFarmByIdOrName(availableFarms, farmId), [availableFarms, farmId])
  const availableProductionUnits = useMemo(() => selectedFarm?.productionUnits ?? [], [selectedFarm])
  const selectedProductionUnit = useMemo(
    () => findProductionUnitByIdOrName(selectedFarm, productionUnitId),
    [selectedFarm, productionUnitId]
  )
  const {
    lots,
    loading: lotsLoading,
    upsertLot
  } = useLots({ farmId, productionUnitId, enabled: Boolean(farmId) })
  const selectedLot = useMemo(
    () => findLotByDashboardSelection(lotId, lots) ?? undefined,
    [lotId, lots]
  )
  const selectedLotIsInteractive = selectedLot ? isLotActiveForInteraction(selectedLot.stage) : false

  const selectedPresetKey = useMemo(() => resolveSfrMatrixPresetKey(selectedSpecies), [selectedSpecies])
  const facilityProfiles = useMemo(() => listFarmMortalityProfiles(selectedFarm, selectedSpecies), [selectedFarm, selectedSpecies])
  const selectedProfile = useMemo(
    () => facilityProfiles.find((profile) => profile.id === selectedProfileId) ?? null,
    [facilityProfiles, selectedProfileId]
  )
  const appliedProfileSelection = useMemo(
    () => resolveMortalityProfileSelection({
      farm: selectedFarm,
      productionUnit: selectedProductionUnit,
      lot: selectedLot,
      species: selectedSpecies
    }),
    [selectedFarm, selectedLot, selectedProductionUnit, selectedSpecies]
  )
  const facilityDefaultProfile = useMemo(
    () => findFarmMortalityProfile(selectedFarm, readFarmDefaultMortalityProfileId(selectedFarm, selectedSpecies)),
    [selectedFarm, selectedSpecies]
  )
  const productionUnitAssignedProfile = useMemo(
    () => findFarmMortalityProfile(selectedFarm, readAssignedMortalityProfileId(selectedProductionUnit, selectedSpecies)),
    [selectedFarm, selectedProductionUnit, selectedSpecies]
  )
  const lotAssignedProfile = useMemo(
    () => findFarmMortalityProfile(selectedFarm, readAssignedMortalityProfileId(selectedLot, selectedSpecies)),
    [selectedFarm, selectedLot, selectedSpecies]
  )
  const isEditingFacilityProfile = Boolean(selectedProfile)

  const speciesOptions = useMemo(
    () => SPECIES_KEYS.map((value) => ({ value, label: t(SPECIES_I18N[value].labelKey) })),
    [t]
  )

  const profileOptions = useMemo(
    () => [
      {
        value: PROFILE_EDITOR_SPECIES_DEFAULT,
        label: t('mortalityManagement.profile.option.speciesDefault')
      },
      ...facilityProfiles.map((profile) => ({
        value: profile.id,
        label: profile.name
      }))
    ],
    [facilityProfiles, t]
  )

  const selectedSpeciesDescription = useMemo(() => {
    const entry = SPECIES_I18N[selectedSpecies]
    return entry ? t(entry.descriptionKey) : t('sfrManagement.species.descriptionFallback')
  }, [selectedSpecies, t])

  const selectedSpeciesLabel = useMemo(() => {
    const option = speciesOptions.find((item) => item.value === selectedSpecies)
    return option?.label ?? selectedSpecies
  }, [selectedSpecies, speciesOptions])

  const selectedFarmLabel = useMemo(() => {
    if (!farmId) return '—'
    return selectedFarm?.name || String(farmId)
  }, [farmId, selectedFarm])

  const selectedProductionUnitLabel = useMemo(() => {
    if (!selectedFarm) return '—'
    if (!productionUnitId) return t('warehouse.allUnits')
    return selectedProductionUnit?.name || String(productionUnitId)
  }, [productionUnitId, selectedFarm, selectedProductionUnit, t])

  const selectedLotLabel = useMemo(() => {
    if (!productionUnitId) return t('tasks.filter.allLots')
    if (!lotId) return t('tasks.filter.allLots')
    return selectedLot?.lotKey || String(lotId)
  }, [lotId, productionUnitId, selectedLot, t])

  const selectedMatrixLabel = useMemo(
    () => selectedProfile?.name ?? t('mortalityManagement.profile.option.speciesDefault'),
    [selectedProfile, t]
  )

  const profileEditingSummary = useMemo(
    () => selectedProfile
      ? t('mortalityManagement.profile.summary.editingProfile', { name: selectedProfile.name })
      : t('mortalityManagement.profile.summary.editingSpeciesDefault', { species: selectedSpeciesLabel }),
    [selectedProfile, selectedSpeciesLabel, t]
  )

  const profileAppliedSummary = useMemo(() => {
    switch (appliedProfileSelection.source) {
      case 'lot':
        return t('mortalityManagement.profile.summary.appliedLot', {
          profile: appliedProfileSelection.profile?.name ?? t('mortalityManagement.profile.option.speciesDefault'),
          lot: selectedLotLabel
        })
      case 'production-unit':
        return t('mortalityManagement.profile.summary.appliedProductionUnit', {
          profile: appliedProfileSelection.profile?.name ?? t('mortalityManagement.profile.option.speciesDefault'),
          productionUnit: selectedProductionUnitLabel
        })
      case 'facility-default':
        return t('mortalityManagement.profile.summary.appliedFacilityDefault', {
          profile: appliedProfileSelection.profile?.name ?? t('mortalityManagement.profile.option.speciesDefault'),
          facility: selectedFarmLabel
        })
      default:
        return t('mortalityManagement.profile.summary.appliedSpeciesDefault', { species: selectedSpeciesLabel })
    }
  }, [
    appliedProfileSelection.profile?.name,
    appliedProfileSelection.source,
    selectedFarmLabel,
    selectedLotLabel,
    selectedProductionUnitLabel,
    selectedSpeciesLabel,
    t
  ])

  const profileHelpText = useMemo(
    () => selectedFarm
      ? t('mortalityManagement.profile.help.facility')
      : t('mortalityManagement.profile.help.noFacility'),
    [selectedFarm, t]
  )

  const farmOptions = useMemo(
    () => availableFarms.map((farm) => ({ value: String(farm.id), label: farm.name || String(farm.id), key: String(farm.id) })),
    [availableFarms]
  )

  const productionUnitOptions = useMemo(
    () => availableProductionUnits.map((unit) => ({ value: String(unit.id), label: unit.name || String(unit.id), key: String(unit.id) })),
    [availableProductionUnits]
  )

  const lotOptions = useMemo(
    () => lots.map((lot) => ({
      value: String(lot.id),
      label: isLotActiveForInteraction(lot.stage)
        ? (lot.lotKey || String(lot.id))
        : `${lot.lotKey || String(lot.id)} (${t('lots.stage.inactive')})`,
      key: String(lot.id),
      disabled: !isLotActiveForInteraction(lot.stage)
    })),
    [lots, t]
  )

  const lotPlaceholder = useMemo(() => {
    if (productionUnitId) {
      if (lotsLoading) return t('biomass.lot.placeholder.loadingLots')
      if (lotOptions.length === 0) return t('home.selector.lot.placeholder.noneForUnit')
      return t('tasks.filter.allLots')
    }

    return t('home.selector.lot.placeholder.selectUnitFirst')
  }, [lotOptions.length, lotsLoading, productionUnitId, t])

  useEffect(() => {
    let cancelled = false

    const loadContext = async () => {
      const farms = await ensureFarmList()
      if (cancelled) return

      setAvailableFarms(farms)

      const activeContext = loadActiveContextSelection()
      const rememberedFarm = readCurrentFarm()
      const resolvedFarm = findFarmByIdOrName(farms, activeContext.farmId)
        ?? findFarmByIdOrName(farms, rememberedFarm?.id ?? rememberedFarm?.name ?? '')
      const resolvedUnit = findProductionUnitByIdOrName(resolvedFarm, activeContext.productionUnitId)
      const storedSpecies = loadStoredSpecies()
      const nextSpecies = resolveMortalityManagementSpecies(resolvedFarm, storedSpecies)

      setFarmId(resolvedFarm?.id ? String(resolvedFarm.id) : '')
      setProductionUnitId(resolvedUnit?.id ? String(resolvedUnit.id) : '')
      setLotId(activeContext.lotId?.trim() ?? '')
      setSelectedSpecies(nextSpecies)
      persistSpeciesSelection(nextSpecies)
    }

    void loadContext()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    persistSpeciesSelection(selectedSpecies)
  }, [selectedSpecies])

  useEffect(() => {
    if (!productionUnitId && lotId) {
      setLotId('')
      persistActiveContextSelection({ lotId: '' })
      return
    }

    if (lotId && !lotsLoading) {
      const normalizedLotId = normalizeDashboardLotSelection(lotId, lots)
      if (!normalizedLotId) {
        setLotId('')
        persistActiveContextSelection({ lotId: '' })
        return
      }

      if (normalizedLotId !== lotId) {
        setLotId(normalizedLotId)
        persistActiveContextSelection({ lotId: normalizedLotId })
      }
    }
  }, [lotId, lots, lotsLoading, productionUnitId])

  useEffect(() => () => {
    if (profileSaveTimeoutRef.current) {
      globalThis.clearTimeout(profileSaveTimeoutRef.current)
    }
  }, [])

  useEffect(() => {
    if (selectedProfileId !== PROFILE_EDITOR_SPECIES_DEFAULT && facilityProfiles.some((profile) => profile.id === selectedProfileId)) {
      return
    }

    setSelectedProfileId(appliedProfileSelection.profileId ?? PROFILE_EDITOR_SPECIES_DEFAULT)
  }, [appliedProfileSelection.profileId, facilityProfiles, selectedProfileId])

  useEffect(() => {
    skipNextProfileSaveRef.current = true
    setCalculatedMortality(null)

    if (!selectedProfile) {
      setCurrentMatrixData(null)
      setMatrixVersion((prev) => prev + 1)
      return
    }

    setCurrentMatrixData(cloneMortalityMatrix(selectedProfile.matrix))
    setMatrixVersion((prev) => prev + 1)
  }, [selectedProfile?.id, selectedProfile?.updatedAt, selectedSpecies])

  const setSaveErrorStatus = () => {
    setMatrixStatus({
      state: 'error',
      tone: 'error',
      message: t('mortalityManagement.status.saveFailed')
    })
  }

  const showSyncStatus = (syncState: 'offline' | 'pending' | 'synced') => {
    setMatrixStatus(getMortalityMatrixStatusPresentation(syncState, (key) => t(key as TranslationKey), null))
  }

  const handleMutationStatus = (result: {
    syncState: 'offline' | 'pending' | 'synced'
    syncPromise: Promise<'offline' | 'pending' | 'synced'> | null
  }) => {
    showSyncStatus(result.syncState)

    if (!result.syncPromise) {
      return
    }

    void result.syncPromise
      .then((nextSyncState) => {
        showSyncStatus(nextSyncState)
      })
      .catch((error) => {
        console.error('Failed to sync mortality profile mutation', error)
        setSaveErrorStatus()
      })
  }

  const mergeSavedFarm = (savedFarm: WizardFarm) => {
    setAvailableFarms((prev) => {
      const hasExisting = prev.some((farm) => String(farm.id ?? '') === String(savedFarm.id ?? ''))
      if (!hasExisting) {
        return [...prev, savedFarm]
      }

      return prev.map((farm) => String(farm.id ?? '') === String(savedFarm.id ?? '') ? savedFarm : farm)
    })
  }

  const persistFarmMutation = async (baseFarm: WizardFarm, nextFarm: WizardFarm): Promise<WizardFarm | null> => {
    try {
      const result = await saveEditedFarm(nextFarm, baseFarm)
      mergeSavedFarm(result.data)
      handleMutationStatus(result)
      return result.data
    } catch (error) {
      console.error('Failed to save mortality profile metadata', error)
      setSaveErrorStatus()
      return null
    }
  }

  const handleProfileChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedProfileId(event.target.value)
    setCalculatedMortality(null)
    setMatrixStatus(null)
  }

  const handleMatrixChange = (data: MatrixData) => {
    setCurrentMatrixData(data)
  }

  useEffect(() => {
    if (!selectedFarm || !selectedProfile || !currentMatrixData) {
      return
    }

    if (skipNextProfileSaveRef.current) {
      skipNextProfileSaveRef.current = false
      return
    }

    if (matricesEqual(currentMatrixData, selectedProfile.matrix)) {
      return
    }

    if (profileSaveTimeoutRef.current) {
      globalThis.clearTimeout(profileSaveTimeoutRef.current)
    }

    setMatrixStatus(getMortalityMatrixStatusPresentation('saving', (key) => t(key as TranslationKey), null))

    profileSaveTimeoutRef.current = globalThis.setTimeout(async () => {
      const nextProfile = {
        ...selectedProfile,
        matrix: cloneMortalityMatrix(currentMatrixData),
        updatedAt: new Date().toISOString()
      }
      const nextFarm = upsertFarmMortalityProfile(selectedFarm, nextProfile)
      await persistFarmMutation(selectedFarm, nextFarm)
    }, PROFILE_SAVE_DEBOUNCE_MS)

    return () => {
      if (profileSaveTimeoutRef.current) {
        globalThis.clearTimeout(profileSaveTimeoutRef.current)
      }
    }
  }, [currentMatrixData, selectedFarm, selectedProfile, t])

  const handleSpeciesChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const nextSpecies = event.target.value as ProductionSpeciesKey
    const nextPresetKey = resolveSfrMatrixPresetKey(nextSpecies)
    const presetChanged = nextPresetKey !== selectedPresetKey
    setSelectedSpecies(nextSpecies)
    setSelectedProfileId(PROFILE_EDITOR_SPECIES_DEFAULT)
    setMatrixStatus(null)
    persistSpeciesSelection(nextSpecies)

    if (presetChanged) {
      setCurrentMatrixData(null)
      setCalculatedMortality(null)
      setMatrixVersion((prev) => prev + 1)
    }
  }

  const handleFarmChange = (value: string) => {
    const nextFarm = findFarmByIdOrName(availableFarms, value)
    const nextSpecies = nextFarm ? resolveMortalityManagementSpecies(nextFarm, selectedSpecies) : selectedSpecies

    setFarmId(value)
    setProductionUnitId('')
    setLotId('')
    setSelectedSpecies(nextSpecies)
    setSelectedProfileId(PROFILE_EDITOR_SPECIES_DEFAULT)
    setMatrixStatus(null)
    persistSpeciesSelection(nextSpecies)
    persistActiveContextSelection({ farmId: value, productionUnitId: '', lotId: '' })
  }

  const handleProductionUnitChange = (value: string) => {
    setProductionUnitId(value)
    setLotId('')
    setSelectedProfileId(PROFILE_EDITOR_SPECIES_DEFAULT)
    setMatrixStatus(null)
    persistActiveContextSelection({ productionUnitId: value, lotId: '' })
  }

  const handleLotChange = (value: string) => {
    setLotId(value)
    setSelectedProfileId(PROFILE_EDITOR_SPECIES_DEFAULT)
    setMatrixStatus(null)
    persistActiveContextSelection({ lotId: value })
  }

  const handleCreateFacilityProfile = async () => {
    if (!selectedFarm) {
      alert(t('mortalityManagement.profile.alert.selectFacility'))
      return
    }

    if (!currentMatrixData) {
      return
    }

    const suggestedName = [selectedFarm.name, selectedProductionUnit?.name, selectedLot?.lotKey, selectedSpeciesLabel]
      .map((value) => String(value ?? '').trim())
      .filter(Boolean)
      .join(' - ')
    const nextName = window.prompt(t('mortalityManagement.profile.prompt.name'), suggestedName)?.trim()

    if (!nextName) {
      return
    }

    const timestamp = new Date().toISOString()
    const nextProfile = {
      id: createId('mortality-profile'),
      species: selectedSpecies,
      name: nextName,
      matrix: cloneMortalityMatrix(currentMatrixData),
      createdAt: timestamp,
      updatedAt: timestamp
    }
    const nextFarm = upsertFarmMortalityProfile(selectedFarm, nextProfile)
    const savedFarm = await persistFarmMutation(selectedFarm, nextFarm)

    if (!savedFarm) {
      return
    }

    setSelectedProfileId(nextProfile.id)
  }

  const handleSetFacilityDefault = async (profileId: string | null) => {
    if (!selectedFarm) {
      alert(t('mortalityManagement.profile.alert.selectFacility'))
      return
    }

    const nextFarm = setFarmDefaultMortalityProfileId(selectedFarm, selectedSpecies, profileId)
    await persistFarmMutation(selectedFarm, nextFarm)
  }

  const handleApplyToProductionUnit = async (profileId: string | null) => {
    if (!selectedFarm) {
      alert(t('mortalityManagement.profile.alert.selectFacility'))
      return
    }

    if (!selectedProductionUnit) {
      alert(t('mortalityManagement.profile.alert.selectProductionUnit'))
      return
    }

    const nextFarm = {
      ...selectedFarm,
      productionUnits: (selectedFarm.productionUnits ?? []).map((unit) => (
        String(unit.id ?? '') === String(selectedProductionUnit.id ?? '')
          ? setProductionUnitMortalityProfileAssignment(unit, selectedSpecies, profileId)
          : unit
      ))
    }

    await persistFarmMutation(selectedFarm, nextFarm)
  }

  const handleApplyToLot = async (profileId: string | null) => {
    if (!selectedLot) {
      alert(t('mortalityManagement.profile.alert.selectLot'))
      return
    }

    if (!selectedLotIsInteractive) {
      alert(t('mortalityManagement.profile.alert.activeLotRequired'))
      return
    }

    const nextLot = setLotMortalityProfileAssignment(selectedLot, selectedSpecies, profileId)

    try {
      const result = await lotApi.update(selectedLot.id, { metadata: nextLot.metadata })
      upsertLot(result.data)
      handleMutationStatus(result)
    } catch (error) {
      console.error('Failed to save mortality lot profile assignment', error)
      setSaveErrorStatus()
    }
  }

  const calculateTestMortality = () => {
    if (!currentMatrixData) return

    const weight = Number.parseFloat(testWeight)
    const temperature = Number.parseFloat(testTemperature)

    if (Number.isNaN(weight) || Number.isNaN(temperature)) {
      alert(t('mortalityManagement.alert.invalidWeightTemp'))
      return
    }

    const rate = interpolateMortalityRate(weight, temperature, { matrix: currentMatrixData, species: selectedPresetKey })
    setCalculatedMortality(rate)
  }

  // v2.1 finding #83: iOS Chrome silently suppresses `window.confirm()` in
  // some event-handler contexts. `useTwoStepConfirm` mirrors SFRManagement's
  // identical pattern.
  const resetConfirm = useTwoStepConfirm()

  const resetConfirmMessage = useMemo(() => {
    if (selectedProfile) {
      return t('mortalityManagement.profile.confirm.resetProfile', {
        profile: selectedProfile.name,
        species: selectedSpeciesLabel
      })
    }
    return t('mortalityManagement.confirm.resetDefaults')
  }, [selectedProfile, selectedSpeciesLabel, t])

  const resetToDefaults = () => {
    const action = resolveMortalityResetToDefaultsAction(resetConfirm.isPending(RESET_CONFIRM_ID), Boolean(selectedProfile))

    if (action === 'arm') {
      resetConfirm.arm(RESET_CONFIRM_ID)
      return
    }

    if (action === 'applyProfileDefaults') {
      skipNextProfileSaveRef.current = false
      const defaults = getDefaultMatrixForSpecies(selectedPresetKey)
      setCurrentMatrixData(defaults)
      setMatrixVersion((prev) => prev + 1)
      setCalculatedMortality(null)
      resetConfirm.reset()
      return
    }

    const defaults = getDefaultMatrixForSpecies(selectedPresetKey)
    cacheMatrix(selectedPresetKey, defaults)
    markLocalMortalityMatrixEdit(selectedPresetKey)
    setCurrentMatrixData(defaults)
    setMatrixVersion((prev) => prev + 1)
    setCalculatedMortality(null)
    resetConfirm.reset()
  }

  const refreshMatrix = () => {
    if (selectedProfile) {
      skipNextProfileSaveRef.current = true
      setCurrentMatrixData(cloneMortalityMatrix(selectedProfile.matrix))
      setMatrixVersion((prev) => prev + 1)
      setCalculatedMortality(null)
      setMatrixStatus(null)
      return
    }

    setRefreshKey((prev) => prev + 1)
  }

  const updateMatrix = (updater: (matrix: MatrixData) => MatrixData | null) => {
    if (!currentMatrixData) return
    const next = updater(currentMatrixData)
    if (!next) return
    if (!isEditingFacilityProfile) {
      cacheMatrix(selectedPresetKey, next)
      markLocalMortalityMatrixEdit(selectedPresetKey)
    }
    setCurrentMatrixData(next)
    setMatrixVersion((prev) => prev + 1)
  }

  const handleAddWeight = () => {
    if (!currentMatrixData) return
    const parsed = Number(newWeight.replace(',', '.'))
    if (!Number.isFinite(parsed) || parsed <= 0) {
      alert(t('mortalityManagement.alert.invalidNewWeight'))
      return
    }
    if (currentMatrixData.weights.includes(parsed)) {
      alert(t('mortalityManagement.alert.weightExists'))
      return
    }
    updateMatrix((matrix) => {
      const cloned = cloneMortalityMatrix(matrix)
      const insertIndex = cloned.weights.findIndex((value) => value > parsed)
      const targetIndex = insertIndex === -1 ? cloned.weights.length : insertIndex
      const referenceIndex = targetIndex === 0 ? 0 : targetIndex - 1
      const templateRow = cloned.values[referenceIndex] ?? cloned.values[cloned.values.length - 1]
      const newRow = templateRow ? [...templateRow] : cloned.temperatures.map(() => 0)
      cloned.weights.splice(targetIndex, 0, parsed)
      cloned.values.splice(targetIndex, 0, newRow)
      return cloned
    })
    setNewWeight('')
  }

  const handleRemoveWeight = (index: number) => {
    if (!currentMatrixData || currentMatrixData.weights.length <= 1) {
      alert(t('mortalityManagement.alert.minWeightRows'))
      return
    }
    updateMatrix((matrix) => {
      const cloned = cloneMortalityMatrix(matrix)
      cloned.weights.splice(index, 1)
      cloned.values.splice(index, 1)
      return cloned
    })
  }

  const handleAddTemperature = () => {
    if (!currentMatrixData) return
    const parsed = Number(newTemperature.replace(',', '.'))
    if (!Number.isFinite(parsed)) {
      alert(t('mortalityManagement.alert.invalidNewTemperature'))
      return
    }
    if (currentMatrixData.temperatures.includes(parsed)) {
      alert(t('mortalityManagement.alert.temperatureExists'))
      return
    }
    updateMatrix((matrix) => {
      const cloned = cloneMortalityMatrix(matrix)
      const insertIndex = cloned.temperatures.findIndex((value) => value > parsed)
      const targetIndex = insertIndex === -1 ? cloned.temperatures.length : insertIndex
      const referenceIndex = targetIndex === 0 ? 0 : targetIndex - 1
      cloned.temperatures.splice(targetIndex, 0, parsed)
      cloned.values = cloned.values.map((row) => {
        const reference = row[referenceIndex] ?? row[row.length - 1] ?? 0
        const newRow = [...row]
        newRow.splice(targetIndex, 0, reference)
        return newRow
      })
      return cloned
    })
    setNewTemperature('')
  }

  const handleRemoveTemperature = (index: number) => {
    if (!currentMatrixData || currentMatrixData.temperatures.length <= 1) {
      alert(t('mortalityManagement.alert.minTemperatureColumns'))
      return
    }
    updateMatrix((matrix) => {
      const cloned = cloneMortalityMatrix(matrix)
      cloned.temperatures.splice(index, 1)
      cloned.values = cloned.values.map((row) => {
        const nextRow = [...row]
        nextRow.splice(index, 1)
        return nextRow
      })
      return cloned
    })
  }

  const sortedWeights = useMemo(() => currentMatrixData?.weights ?? [], [currentMatrixData])
  const sortedTemperatures = useMemo(
    () => currentMatrixData?.temperatures ?? [],
    [currentMatrixData]
  )

  const exportMatrixData = () => {
    if (!currentMatrixData) return

    const csvContent = [
      [String.raw`Weight\Temperature`, ...currentMatrixData.temperatures.map((temp: number) => `${temp}°C`)].join(','),
      ...currentMatrixData.weights.map((weight: number, rowIndex: number) =>
        [
          `${weight}g`,
          ...currentMatrixData.values[rowIndex].map((v: number) => v.toString().replace('.', ','))
        ].join(',')
      )
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const sanitizedProfileName = selectedProfile?.name
      ?.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
    a.download = sanitizedProfileName
      ? `mortality-matrix-${selectedSpecies}-${sanitizedProfileName}.csv`
      : `mortality-matrix-${selectedSpecies}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <div data-screen="mortality-matrix" className="responsive-container">
      <div className="app-header" style={{ padding: 0 }}>
        <h1>{t('mortalityManagement.title')}</h1>
        <p>{t('mortalityManagement.subtitle')}</p>
      </div>

      <div
        className="form-card"
        style={{
          marginTop: 14,
          marginBottom: 14,
          padding: '12px 16px',
          borderRadius: 10,
          border: '1px solid #fde68a',
          background: '#fffbeb',
          color: '#92400e',
          fontSize: 14,
          lineHeight: 1.5
        }}
      >
        {t('mortalityManagement.note.placeholderDefaults')}
      </div>

      <CurrentContextBar
        style={{ marginTop: 14 }}
        species={{ value: selectedSpeciesLabel, label: t('sfrManagement.speciesPreset.label') }}
        fields={[
          { label: t('warehouse.contextFacility'), value: selectedFarmLabel },
          { label: t('warehouse.contextProductionUnit'), value: selectedProductionUnitLabel },
          { label: t('tasks.context.lot'), value: selectedLotLabel }
        ]}
      />

      <div className="form-card" style={{ marginTop: 14, marginBottom: 20 }}>
        <ContextSelectorBar
          farm={{
            label: t('warehouse.contextFacility'),
            value: farmId,
            options: farmOptions,
            onChange: handleFarmChange,
            placeholder: availableFarms.length === 0 ? t('warehouse.loadingFacilities') : t('warehouse.selectFacility'),
            disabled: availableFarms.length === 0
          }}
          productionUnit={{
            label: t('warehouse.contextProductionUnitOptional'),
            value: productionUnitId,
            options: productionUnitOptions,
            onChange: handleProductionUnitChange,
            placeholder: selectedFarm ? t('warehouse.allUnits') : t('warehouse.selectFacilityFirst'),
            disabled: !selectedFarm
          }}
          lot={{
            label: t('tasks.context.lot'),
            value: lotId,
            options: lotOptions,
            onChange: handleLotChange,
            placeholder: lotPlaceholder,
            disabled: !productionUnitId || lotsLoading
          }}
          summary={
            <div style={{ fontSize: 12, color: '#6b7280', paddingBottom: 8 }}>
              {selectedSpeciesDescription}
            </div>
          }
        />
      </div>

      <div className="responsive-toolbar">
        <div className="field" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{t('sfrManagement.speciesPreset.label')}</label>
          <select
            value={selectedSpecies}
            onChange={handleSpeciesChange}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          >
            {speciesOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {selectedSpeciesDescription}
          </div>
        </div>

        <div className="responsive-actions">
          <GradientButton onClick={refreshMatrix} variant="info" size="sm">
            {t('mortalityManagement.action.refreshMatrix')}
          </GradientButton>

          <GradientButton onClick={exportMatrixData} variant="success" size="sm" disabled={!currentMatrixData}>
            {t('mortalityManagement.action.exportCsv')}
          </GradientButton>

          <GradientButton
            onClick={resetToDefaults}
            variant="danger"
            size="sm"
            title={resetConfirm.isPending(RESET_CONFIRM_ID) ? resetConfirmMessage : undefined}
          >
            {resetConfirm.isPending(RESET_CONFIRM_ID)
              ? t('mortalityManagement.confirm.resetCta')
              : t('mortalityManagement.action.resetDefaults')}
          </GradientButton>
          {resetConfirm.isPending(RESET_CONFIRM_ID) ? (
            <GradientButton onClick={() => resetConfirm.reset()} variant="secondary" size="sm">
              {t('common.cancel')}
            </GradientButton>
          ) : null}
        </div>
      </div>

      <div className="form-card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'grid', gap: 14 }}>
          <div style={{ display: 'grid', gap: 6 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
              {t('mortalityManagement.profile.label')}
            </label>
            <select
              value={selectedProfileId}
              onChange={handleProfileChange}
              disabled={!selectedFarm}
              style={{
                width: '100%',
                padding: '10px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                backgroundColor: selectedFarm ? '#ffffff' : '#f9fafb'
              }}
            >
              {profileOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {profileHelpText}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gap: 4,
              padding: '12px 14px',
              borderRadius: '10px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              fontSize: '12px',
              color: '#475569'
            }}
          >
            <div style={{ fontWeight: 600, color: '#0f172a' }}>{selectedMatrixLabel}</div>
            <div>{profileEditingSummary}</div>
            <div>{profileAppliedSummary}</div>
            {facilityDefaultProfile && (
              <div>
                {t('mortalityManagement.profile.summary.facilityDefaultHint', { profile: facilityDefaultProfile.name })}
              </div>
            )}
            {selectedProductionUnit && productionUnitAssignedProfile && (
              <div>
                {t('mortalityManagement.profile.summary.productionUnitHint', {
                  profile: productionUnitAssignedProfile.name,
                  productionUnit: selectedProductionUnitLabel
                })}
              </div>
            )}
            {selectedLot && lotAssignedProfile && (
              <div>
                {t('mortalityManagement.profile.summary.lotHint', {
                  profile: lotAssignedProfile.name,
                  lot: selectedLotLabel
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <GradientButton
              onClick={handleCreateFacilityProfile}
              variant="secondary"
              size="sm"
              disabled={!selectedFarm || !currentMatrixData}
            >
              {t('mortalityManagement.profile.action.saveAsFacilityProfile')}
            </GradientButton>

            <GradientButton
              onClick={() => handleSetFacilityDefault(selectedProfile ? selectedProfile.id : null)}
              variant="info"
              size="sm"
              disabled={selectedProfile ? !selectedFarm : !selectedFarm || !facilityDefaultProfile}
            >
              {selectedProfile
                ? t('mortalityManagement.profile.action.setFacilityDefault')
                : t('mortalityManagement.profile.action.clearFacilityDefault')}
            </GradientButton>

            <GradientButton
              onClick={() => handleApplyToProductionUnit(selectedProfile?.id ?? null)}
              variant="success"
              size="sm"
              disabled={!selectedProductionUnit || !selectedProfile}
            >
              {t('mortalityManagement.profile.action.applyToProductionUnit')}
            </GradientButton>

            <GradientButton
              onClick={() => handleApplyToProductionUnit(null)}
              variant="warning"
              size="sm"
              disabled={!selectedProductionUnit || !productionUnitAssignedProfile}
            >
              {t('mortalityManagement.profile.action.clearProductionUnit')}
            </GradientButton>

            <GradientButton
              onClick={() => handleApplyToLot(selectedProfile?.id ?? null)}
              variant="success"
              size="sm"
              disabled={!selectedLot || !selectedProfile || !selectedLotIsInteractive}
            >
              {t('mortalityManagement.profile.action.applyToLot')}
            </GradientButton>

            <GradientButton
              onClick={() => handleApplyToLot(null)}
              variant="warning"
              size="sm"
              disabled={!selectedLot || !lotAssignedProfile || !selectedLotIsInteractive}
            >
              {t('mortalityManagement.profile.action.clearLot')}
            </GradientButton>
          </div>
        </div>
      </div>

      {matrixStatus && (
        <div aria-live="polite" role="status" style={statusStyle(matrixStatus.tone)}>
          {matrixStatus.message}
        </div>
      )}

      {/* Test Calculator */}
      <div style={{
        backgroundColor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#1f2937',
          margin: '0 0 16px 0'
        }}>
          {t('mortalityManagement.calculator.title')}
        </h3>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              {t('mortalityManagement.calculator.weightLabel')}
            </label>
            <input
              type="number"
              step="0.1"
              value={testWeight}
              onChange={(e) => setTestWeight(e.target.value)}
              placeholder={t('mortalityManagement.calculator.weightPlaceholder')}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                width: '110px'
              }}
            />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>g</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>
              {t('mortalityManagement.calculator.temperatureLabel')}
            </label>
            <input
              type="number"
              step="0.1"
              value={testTemperature}
              onChange={(e) => setTestTemperature(e.target.value)}
              placeholder={t('mortalityManagement.calculator.temperaturePlaceholder')}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                width: '100px'
              }}
            />
            <span style={{ fontSize: '14px', color: '#6b7280' }}>°C</span>
          </div>

          <GradientButton onClick={calculateTestMortality} variant="info" size="sm">
            {t('mortalityManagement.calculator.calculate')}
          </GradientButton>

          {calculatedMortality !== null && (
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#dbeafe',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#1e40af'
            }}>
              {t('mortalityManagement.calculator.result', { value: calculatedMortality.toFixed(4).replace('.', ',') })}
            </div>
          )}
        </div>

        <p style={{
          margin: '12px 0 0 0',
          fontSize: '12px',
          color: '#6b7280'
        }}>
          {t('mortalityManagement.calculator.help')}
        </p>
      </div>

      {/* Mortality Matrix */}
      <MortalityMatrix
        onStatusChange={setMatrixStatus}
        onMatrixChange={handleMatrixChange}
        species={selectedPresetKey}
        refreshKey={refreshKey}
        externalMatrix={isEditingFacilityProfile ? currentMatrixData ?? undefined : undefined}
        externalMatrixVersion={isEditingFacilityProfile ? matrixVersion : undefined}
        persistenceMode={isEditingFacilityProfile ? 'external' : 'species'}
      />

      {currentMatrixData && (
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '12px',
            padding: '20px',
            marginTop: '20px',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
          }}
        >
          <h3
            style={{
              fontSize: '16px',
              fontWeight: 600,
              color: '#1f2937',
              margin: '0 0 16px 0'
            }}
          >
            {t('mortalityManagement.tools.title')}
          </h3>

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '24px'
            }}
          >
            <div style={{ flex: '1 1 280px' }}>
              <h4
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1f2937',
                  margin: '0 0 12px 0'
                }}
              >
                {t('mortalityManagement.tools.weightRows')}
              </h4>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}
              >
                <input
                  type="number"
                  step="0.1"
                  value={newWeight}
                  onChange={(event) => setNewWeight(event.target.value)}
                  placeholder={t('mortalityManagement.tools.addWeightPlaceholder')}
                  style={{
                    flex: '1 1 auto',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <GradientButton onClick={handleAddWeight} variant="info" size="xs">
                  {t('mortalityManagement.tools.addRow')}
                </GradientButton>
              </div>

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                {sortedWeights.map((weight, index) => (
                  <li
                    key={`${weight}-${index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '8px 12px'
                    }}
                  >
                    <span style={{ fontSize: '13px', color: '#1f2937' }}>{weight} g</span>
                    <GradientButton
                      onClick={() => handleRemoveWeight(index)}
                      variant="danger"
                      size="xxs"
                    >
                      {t('mortalityManagement.tools.remove')}
                    </GradientButton>
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ flex: '1 1 280px' }}>
              <h4
                style={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#1f2937',
                  margin: '0 0 12px 0'
                }}
              >
                {t('mortalityManagement.tools.temperatureColumns')}
              </h4>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '12px'
                }}
              >
                <input
                  type="number"
                  step="0.1"
                  value={newTemperature}
                  onChange={(event) => setNewTemperature(event.target.value)}
                  placeholder={t('mortalityManagement.tools.addTemperaturePlaceholder')}
                  style={{
                    flex: '1 1 auto',
                    padding: '8px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px'
                  }}
                />
                <GradientButton onClick={handleAddTemperature} variant="info" size="xs">
                  {t('mortalityManagement.tools.addColumn')}
                </GradientButton>
              </div>

              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px'
                }}
              >
                {sortedTemperatures.map((temperature, index) => (
                  <li
                    key={`${temperature}-${index}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      backgroundColor: '#f9fafb',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      padding: '8px 12px'
                    }}
                  >
                    <span style={{ fontSize: '13px', color: '#1f2937' }}>{temperature} °C</span>
                    <GradientButton
                      onClick={() => handleRemoveTemperature(index)}
                      variant="danger"
                      size="xxs"
                    >
                      {t('mortalityManagement.tools.remove')}
                    </GradientButton>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="form-card">
        <h4 style={{ marginTop: 0 }}>{t('mortalityManagement.usage.title')}</h4>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 14 }}>
          <li>{t('mortalityManagement.usage.item1')}</li>
          <li>{t('mortalityManagement.usage.item2')}</li>
          <li>{t('mortalityManagement.usage.item3')}</li>
          <li>{t('mortalityManagement.usage.item4')}</li>
        </ul>
      </div>
    </div>
  )
}
